/**
 * D360 custom portfolios — the LIVE platform portfolios service.
 *
 *   LIST:    GET {BASE}/portfolios/v3/portfolios       → { custom_portfolios: [...] }
 *   MEMBERS: GET {BASE}/portfolios/v1/portfolios/{id}  → { portfolio_id, name, portfolio_items: string[] }
 *
 * `{BASE}` is the platform host (VITE_API_BASE_URL) — these paths are absolute
 * on that host, NOT under /streaming-economics. The browser calls them directly
 * with the `x-auth-token` session token (confirmed live).
 *
 * This is the streaming-economics port of licensing-intelligence's portfolio
 * layer, trimmed to streaming's needs: plain-TS parsing (no zod — not a
 * dependency here), D360 only (no licensing "client" portfolios), and NO
 * default-resolution logic (the picker starts with none selected).
 */

export type PortfolioType = 'show' | 'movie' | 'sports' | 'content' | 'talent';

const PORTFOLIO_TYPES: ReadonlySet<string> = new Set([
  'show',
  'movie',
  'sports',
  'content',
  'talent',
]);

/** One portfolio as returned by the LIST endpoint (coerced + validated). */
export interface Portfolio {
  /** Numeric id from the API, coerced to string for a stable key everywhere. */
  id: string;
  name: string;
  custom_portfolio_type: PortfolioType;
  is_team_shared?: boolean;
  is_parrot_created?: boolean;
  is_legacy?: boolean;
  /** Creator email; "" for Parrot samples. */
  created_by?: string;
  created_by_name?: string;
  created_on?: string;
  updated_on?: string;
  mode?: string;
}

/** Full flat membership of a portfolio (parrot_id strings). */
export interface PortfolioMembers {
  portfolio_id: string;
  name?: string;
  portfolio_items: string[];
}

// ── Parsing (defensive: a malformed row is dropped, never thrown) ──────────

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

const asString = (v: unknown): string | undefined =>
  typeof v === 'string' ? v : typeof v === 'number' ? String(v) : undefined;

const asBool = (v: unknown): boolean | undefined => (typeof v === 'boolean' ? v : undefined);

/** Parse one raw portfolio; returns null when required fields are missing. */
export function parsePortfolio(raw: unknown): Portfolio | null {
  if (!isRecord(raw)) return null;
  const id = asString(raw.id);
  const name = asString(raw.name);
  const type = raw.custom_portfolio_type;
  if (
    id === undefined ||
    name === undefined ||
    typeof type !== 'string' ||
    !PORTFOLIO_TYPES.has(type)
  ) {
    return null;
  }
  return {
    id,
    name,
    custom_portfolio_type: type as PortfolioType,
    is_team_shared: asBool(raw.is_team_shared),
    is_parrot_created: asBool(raw.is_parrot_created),
    is_legacy: asBool(raw.is_legacy),
    created_by: asString(raw.created_by),
    created_by_name: asString(raw.created_by_name),
    created_on: asString(raw.created_on),
    updated_on: asString(raw.updated_on),
    mode: asString(raw.mode),
  };
}

/** Parse the LIST envelope `{ custom_portfolios: [...] }`. */
export function parsePortfolioList(raw: unknown): Portfolio[] {
  const items = isRecord(raw) && Array.isArray(raw.custom_portfolios) ? raw.custom_portfolios : [];
  return items.map(parsePortfolio).filter((p): p is Portfolio => p !== null);
}

/** Parse the v1 MEMBERS response `{ portfolio_id, name, portfolio_items: [] }`. */
export function parsePortfolioMembers(raw: unknown): PortfolioMembers {
  if (!isRecord(raw)) return { portfolio_id: '', portfolio_items: [] };
  const items = Array.isArray(raw.portfolio_items)
    ? raw.portfolio_items.filter((x): x is string => typeof x === 'string')
    : [];
  return {
    portfolio_id: asString(raw.portfolio_id) ?? '',
    name: asString(raw.name),
    portfolio_items: items,
  };
}

// ── Picker list rules — customer title portfolios only, the user's own first ──
//
// v3 `/portfolios` returns every portfolio on the ACCOUNT, including Parrot's
// SAMPLE portfolios (`is_parrot_created: true`, `created_by: ""`,
// `created_by_name: "Parrot Analytics"`). Customer portfolios omit the flag and
// carry the creator's email in `created_by`. Do NOT filter by email domain:
// support users on @parrotanalytics.com own real customer portfolios.

/** Streaming is titles-only: TV, movies, and mixed (content). */
const TITLE_TYPES: ReadonlySet<PortfolioType> = new Set(['show', 'movie', 'content']);

/** Legacy Parrot-admin seed portfolios (pre-`is_parrot_created`). */
const ADMIN_EMAIL = 'admin@parrotanalytics.com';
const PARROT_CREATOR_NAME = 'parrot analytics';

const norm = (s: string | undefined) => (s ?? '').trim().toLowerCase();

/** True for Parrot-made sample/seed portfolios that must be hidden. */
export function isParrotSample(p: Portfolio): boolean {
  if (p.is_parrot_created === true) return true;
  const by = norm(p.created_by);
  if (by === '' && norm(p.created_by_name) === PARROT_CREATOR_NAME) return true;
  return by === ADMIN_EMAIL;
}

/** Customer title portfolios only (no Parrot samples, no talent/sports). */
export function filterCustomerPortfolios(list: Portfolio[]): Portfolio[] {
  return list.filter((p) => TITLE_TYPES.has(p.custom_portfolio_type) && !isParrotSample(p));
}

const ts = (s: string | undefined) => Date.parse(s ?? '') || 0;

/** updated_on desc → created_on desc → name asc. */
export function byRecency(a: Portfolio, b: Portfolio): number {
  return (
    ts(b.updated_on) - ts(a.updated_on) ||
    ts(b.created_on) - ts(a.created_on) ||
    a.name.localeCompare(b.name)
  );
}

export type PortfolioGroups = { mine: Portfolio[]; team: Portfolio[] };

/**
 * Split into the user's own D360 portfolios (created_by === userEmail,
 * case-insensitive) and the team's D360 portfolios, both sorted by recency.
 * Unknown user → every portfolio is a team portfolio.
 */
export function groupPortfolios(list: Portfolio[], userEmail?: string | null): PortfolioGroups {
  const me = norm(userEmail ?? undefined);
  const mine: Portfolio[] = [];
  const team: Portfolio[] = [];
  for (const p of list) {
    (me && norm(p.created_by) === me ? mine : team).push(p);
  }
  mine.sort(byRecency);
  team.sort(byRecency);
  return { mine, team };
}

/** Flat picker order: own portfolios, then team. */
export function orderPortfolios(list: Portfolio[], userEmail?: string | null): Portfolio[] {
  const { mine, team } = groupPortfolios(list, userEmail);
  return [...mine, ...team];
}
